import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Icon } from '@blueprintjs/core';
import {
  EWarrantDecisionStatus, EWarrantDocument, EWarrantStatus
} from 'data/eWarrant/eWarrant.types';
import { DocumentDecisions } from 'routes/EWarrant/utils/documentDecisions';
import formatBytes from 'routes/EWarrant/utils/formatBytes';
import './e-warrant-document-table.scss';

const CHECKSUM_PREVIEW_LENGTH = 16;

type Props = {
  documents: EWarrantDocument[]
  /** What the operator has decided per document, keyed by docId. */
  decisions: DocumentDecisions
  /**
   * False once the e-warrant itself has been decided, or while a decision is in flight —
   * the two columns then read out the decision Xcipio holds rather than offering a change.
   */
  isDecidable: boolean
  onDownload: (document: EWarrantDocument) => void
  onDecide: (docId: string, status: EWarrantDecisionStatus) => void
};

const EWarrantDocumentTable: FunctionComponent<Props> = ({
  documents,
  decisions,
  isDecidable,
  onDownload,
  onDecide
}: Props) => {
  const { t } = useTranslation();

  if (documents.length === 0) {
    return (
      <p className="e-warrant-document-table-empty">{t('eWarrant.view.noDocuments')}</p>
    );
  }

  const checksumType = documents[0].checksumType || 'sha-256';

  /**
   * One of a document's two decision boxes. Both are rendered from here so the pair cannot
   * drift apart, and because the decision is one value per document rather than a flag per
   * box, ticking either one is what clears the other.
   *
   * onChange rather than onClick: the box is fully controlled, so a click on the box that
   * is already ticked comes through here too and the handler clears it back to undecided.
   */
  const renderDecisionCell = (document: EWarrantDocument, status: EWarrantDecisionStatus) => {
    const isApproval = status === EWarrantStatus.Approved;
    const modifier = (isApproval ? 'approved' : 'rejected');

    return (
      <td className="e-warrant-document-decision-cell">
        <Checkbox
          className={`e-warrant-document-decision e-warrant-document-decision-${modifier}`}
          data-testid={`document-${modifier}-${document.docId}`}
          checked={decisions[document.docId] === status}
          // A document that reached the UI without a docId cannot be addressed in the
          // decision payload, so there is nothing a tick on it could record.
          disabled={!isDecidable || !document.docId}
          aria-label={t(
            isApproval ? 'eWarrant.view.document.approveLabel' : 'eWarrant.view.document.rejectLabel',
            { name: document.documentName },
          )}
          onChange={() => onDecide(document.docId, status)}
        />
      </td>
    );
  };

  return (
    <table className="e-warrant-document-table" data-testid="EWarrantDocumentTable">
      <thead>
        <tr>
          <th>{t('eWarrant.view.document.name')}</th>
          <th>{t('eWarrant.view.document.type')}</th>
          <th>{t('eWarrant.view.document.contentType')}</th>
          <th>{t('eWarrant.view.document.checksum', { checksumType })}</th>
          <th>{t('eWarrant.view.document.size')}</th>
          <th className="e-warrant-document-decision-head">
            {t('eWarrant.view.document.approved')}
          </th>
          <th className="e-warrant-document-decision-head">
            {t('eWarrant.view.document.rejected')}
          </th>
          <th aria-label={t('eWarrant.view.document.actions')} />
        </tr>
      </thead>
      <tbody>
        {documents.map((document) => (
          <tr key={document.docId}>
            <td>
              <span className="e-warrant-document-name">
                {document.isPrimary && (
                  <Icon icon="star" className="e-warrant-document-primary-icon" />
                )}
                {document.documentName}
                {document.isPrimary && (
                  <span className="e-warrant-document-primary-badge">
                    {t('eWarrant.view.document.primary')}
                  </span>
                )}
              </span>
            </td>
            <td>{document.documentType || '—'}</td>
            <td className="monospaced">{document.contentType || '—'}</td>
            <td className="monospaced muted" title={document.checksum}>
              {document.checksum ? `${document.checksum.slice(0, CHECKSUM_PREVIEW_LENGTH)}…` : '—'}
            </td>
            <td>{formatBytes(document.sizeBytes)}</td>
            {renderDecisionCell(document, EWarrantStatus.Approved)}
            {renderDecisionCell(document, EWarrantStatus.Rejected)}
            <td>
              <Button
                minimal
                small
                icon="download"
                // hasContent covers a document with the file embedded inline in its ETSI
                // payload; documentLocation covers the far more common case, a file Xcipio
                // holds on disk and only points at (DOCUMENTLOC) - either is downloadable.
                disabled={!document.hasContent && !document.documentLocation}
                text={t('eWarrant.view.document.download')}
                onClick={() => onDownload(document)}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default EWarrantDocumentTable;
